import { ONE_DAY_MS } from './cartesianChart'
import type { TimeSeriesDataPoint } from './types'

/** Bucket size for a time-series bar/line: exact day, ISO week, or calendar month. */
export type BucketGranularity = 'day' | 'week' | 'month'

/** Widest range still shown as daily bars before switching to weekly (~1 month). */
const DAILY_MAX_DAYS = 31
/** Widest range still shown as weekly buckets before switching to monthly (~1 quarter). */
const WEEKLY_MAX_DAYS = 92

/**
 * Choose a bucket size from the visible range so bars stay readable as the window
 * widens: up to ~1 month → per-day, up to ~1 quarter → weekly, beyond → monthly.
 *
 * These thresholds are deliberately more relaxed than the multi-channel Posts
 * stacked bar (which buckets from 8 days, because it stacks up to ~11 channels
 * and daily would be dense): a single-channel Content Impact chart reads fine
 * with daily bars across a month. Pure.
 */
export function pickBucketGranularity(
  startDate: Date,
  endDate: Date,
): BucketGranularity {
  const days =
    Math.round((endDate.getTime() - startDate.getTime()) / ONE_DAY_MS) + 1
  if (days <= DAILY_MAX_DAYS) return 'day'
  if (days <= WEEKLY_MAX_DAYS) return 'week'
  return 'month'
}

/** Normalise any ISO date to UTC midnight of that calendar day. */
function toUtcDay(iso: string): Date {
  const d = new Date(iso)
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}

/** Start of the bucket a day falls in: the day itself, its Monday, or the 1st. */
function bucketStart(day: Date, granularity: BucketGranularity): Date {
  if (granularity === 'month') {
    return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1))
  }
  if (granularity === 'day') return day
  // Week → ISO week start (Monday). getUTCDay: 0 = Sunday … 6 = Saturday.
  const daysSinceMonday = (day.getUTCDay() + 6) % 7
  const monday = new Date(day)
  monday.setUTCDate(monday.getUTCDate() - daysSinceMonday)
  return monday
}

function isoDay(day: Date): string {
  return day.toISOString().slice(0, 10)
}

interface Bucket {
  points: TimeSeriesDataPoint[]
}

/** Group points into buckets keyed by bucket-start (UTC), preserving chronological
 *  order. The comparison `originalDate`, when present, is carried as the bucket
 *  start of the group's first point so a comparison overlay stays aligned. */
function groupByBucket(
  points: TimeSeriesDataPoint[],
  granularity: BucketGranularity,
): Array<{ date: string; originalDate?: string; bucket: Bucket }> {
  const sorted = [...points].toSorted(
    (a, b) => toUtcDay(a.date).getTime() - toUtcDay(b.date).getTime(),
  )
  // `groups` holds the buckets in first-seen (chronological) order; `byKey` finds
  // an existing bucket to append to. Both reference the same Bucket object, so
  // points pushed after creation land in the group already in `groups`.
  const groups: Array<{ date: string; originalDate?: string; bucket: Bucket }> =
    []
  const byKey = new Map<string, Bucket>()
  for (const point of sorted) {
    const key = isoDay(bucketStart(toUtcDay(point.date), granularity))
    let bucket = byKey.get(key)
    if (!bucket) {
      bucket = { points: [] }
      byKey.set(key, bucket)
      const originalDate = point.originalDate
        ? isoDay(bucketStart(toUtcDay(point.originalDate), granularity))
        : undefined
      groups.push({ date: key, originalDate, bucket })
    }
    bucket.points.push(point)
  }
  return groups
}

/**
 * Sum a daily **count** series (e.g. Posts) into day/week/month buckets, keyed by
 * bucket start (UTC). A null day counts as 0 — a gap is "no bar", not missing
 * data. For count metrics only; never use for cumulative series like followers.
 * Pure.
 */
export function bucketSum(
  points: TimeSeriesDataPoint[],
  granularity: BucketGranularity,
): TimeSeriesDataPoint[] {
  return groupByBucket(points, granularity).map(
    ({ date, originalDate, bucket }) =>
      Object.assign(
        {
          date,
          value: bucket.points.reduce((sum, p) => sum + (p.value ?? 0), 0),
        },
        originalDate ? { originalDate } : {},
      ),
  )
}

/**
 * Bucket a daily **cumulative** series (e.g. Followers) into day/week/month
 * buckets, taking the **last observed value** in each bucket (the end-of-period
 * level) — cumulative values must never be summed. A bucket with no observed
 * value stays null (renders as a no-data gap). Pure.
 */
export function bucketLast(
  points: TimeSeriesDataPoint[],
  granularity: BucketGranularity,
): TimeSeriesDataPoint[] {
  return groupByBucket(points, granularity).map(
    ({ date, originalDate, bucket }) => {
      const lastObserved = [...bucket.points]
        .toReversed()
        .find((p) => p.value != null)
      return Object.assign(
        {
          date,
          value: lastObserved?.value ?? null,
        },
        lastObserved?.source ? { source: lastObserved.source } : {},
        originalDate ? { originalDate } : {},
      )
    },
  )
}
